"use client";

import { Stethoscope, Microscope, Sparkles, Smile, BrainCircuit, Laptop, Dog, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductCategoryFilters() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tripleCards = [{
    title: "人工智能醫療",
    description: "先進診斷演算法與預測分析，增強臨床決策與患者療效。",
    spec: "規格：臨床級人工智能",
    icon: BrainCircuit
  }, {
    title: "數字醫療",
    description: "整合軟體解決方案，實現無縫的醫院管理、遠距醫療與患者數據連接。",
    spec: "規格：HL7 / FHIR 符合",
    icon: Laptop
  }, {
    title: "獸醫設備",
    description: "為獸醫診所量身打造的專門醫療設備，確保動物健康的高精密照護。",
    spec: "規格：獸醫特定設計",
    icon: Dog
  }];
  return <section data-section-id="2246" id="productcategoryfilters" className="bg-background text-foreground py-24 md:py-32 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-block w-2 h-2 rounded-md bg-blue-600" />
              <span className="font-default text-xs tracking-widest text-blue-600 uppercase">
                我們的產品組合
              </span>
            </div>
            <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.05] tracking-tight">
              創新{" "}
              <span className="font-default italic font-normal text-blue-600">醫療解決方案。</span>
            </h2>
          </div>
          <div className="flex items-end">
            <p className="font-default text-base sm:text-lg text-slate-600 leading-relaxed max-w-md">
              我們精選並配銷全球領先公司的優質醫療技術，確保醫療專業人員能取得最可靠且臨床價值最高的工具。
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-5 transition-all duration-700 delay-100 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Card data-index={0} className="lg:col-span-7 lg:row-span-2 border border-slate-200 bg-white overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative py-0">
            <CornerBracket position="tr" />
            <CornerBracket position="tl" />

            <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_devices_suite.webp" alt="Advanced surgical equipment in a modern operating room" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
              <div className="absolute top-4 left-4">
                <Badge className="font-default text-[10px] tracking-widest bg-white/80 text-slate-900 border border-slate-200 backdrop-blur-sm rounded-md px-2 py-1">
                  ISO 13485 CERTIFIED
                </Badge>
              </div>
            </div>

            <div className="p-6 md:p-8 lg:p-10 flex flex-col gap-4">
              <div className="w-12 h-12 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Stethoscope className="w-7 h-7 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-default text-2xl md:text-3xl font-semibold tracking-tight">
                手術與診斷設備
              </h3>
              <p className="font-default text-sm md:text-base text-slate-600 leading-relaxed max-w-lg">
                高精密手術儀器與診斷系統，旨在提升臨床環境中的手術準確性與患者安全性。
              </p>
              <div className="mt-2 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="font-default text-[11px] tracking-widest text-slate-500 uppercase">
                  規格：臨床級
                </span>
                <span className="font-default text-[11px] tracking-widest text-blue-600 uppercase">
                  01 / 08
                </span>
              </div>
            </div>
          </Card>

          <Card data-index={1} className="lg:col-span-5 border border-slate-200 bg-white overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative p-6 md:p-7">
            <CornerBracket position="tr" />

            <div className="flex flex-col gap-4 h-full">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Microscope className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <Badge className="font-default text-[10px] tracking-widest bg-blue-600 text-white rounded-md">
                  GLOBAL SOURCING
                </Badge>
              </div>

              <h3 className="font-default text-xl font-semibold tracking-tight">
                通用醫療設備
              </h3>
              <p className="font-default text-sm text-slate-600 leading-relaxed">
                來自可信任全球製造商的全方位必需醫療設備，支援日常臨床運作。
              </p>

              <div className="mt-auto pt-4 space-y-2">
                <div className="flex justify-between font-default text-[10px] tracking-widest text-slate-500 uppercase">
                  <span>可用性</span>
                  <span className="text-blue-600">全球配銷</span>
                </div>
                <Progress value={100} className="h-1 bg-slate-100" />
                <div className="pt-2 border-t border-slate-100">
                  <span className="font-default text-[10px] tracking-widest text-slate-500 uppercase">
                    規格：法規符合
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card data-index={2} className="lg:col-span-5 border border-slate-200 bg-white overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative py-0">
            <CornerBracket position="tr" />

            <div className="relative h-40 overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetics_dental_solutions.webp" alt="Modern medical aesthetics and dental equipment" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
            </div>

            <div className="p-6 md:p-7 pt-2 flex flex-col gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-default text-xl font-semibold tracking-tight">
                美容與牙科
              </h3>
              <p className="font-default text-sm text-slate-600 leading-relaxed">
                醫療美容與牙科護理的優質解決方案，著重於創新與患者滿意度。
              </p>
              <div className="mt-2 pt-4 border-t border-slate-100">
                <span className="font-default text-[10px] tracking-widest text-slate-500 uppercase">
                  規格：高性能
                </span>
              </div>
            </div>
          </Card>

          {tripleCards.map((card, i) => {
          const Icon = card.icon;
          return <Card key={card.title} data-index={i} className="lg:col-span-4 border border-slate-200 bg-white group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 relative p-6 md:p-7">
                <CornerBracket position="tr" />

                <div className="flex flex-col gap-3 h-full">
                  <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-default text-xl font-semibold tracking-tight">
                    {card.title}
                  </h3>
                  <p className="font-default text-sm text-slate-600 leading-relaxed flex-1">
                    {card.description}
                  </p>
                  <div className="mt-2 pt-4 border-t border-slate-100">
                    <span className="font-default text-[10px] tracking-widest text-slate-500 uppercase">
                      {card.spec}
                    </span>
                  </div>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}
function CornerBracket({
  position
}: {
  position: "tr" | "tl" | "br" | "bl";
}) {
  const positions = {
    tr: "top-3 right-3 border-t border-r",
    tl: "top-3 left-3 border-t border-l",
    br: "bottom-3 right-3 border-b border-r",
    bl: "bottom-3 left-3 border-b border-l"
  };
  return <span aria-hidden="true" className={`absolute ${positions[position]} w-3 h-3 border-blue-600/60 pointer-events-none z-10`} />;
}